import axios, { AxiosResponse } from 'axios'
import { ID, Response } from '../../../../../../_metronic/helpers'
import { Contact, ContactsQueryResponse, ORG, UploadedFile } from './_models'

const API_URL = process.env.REACT_APP_API_URL
const CONTACT_URL = `${API_URL}/contact`
const IMPORT_CONTACT_URL = `${API_URL}/contact/bulk`
const PREVIEW_IMPORT_URL = `${API_URL}/contact/bulk/preview`
const VCF_URL = `${API_URL}/contact/vcard`
const GET_CONTACTS_URL = `${API_URL}/contact`
const UPLOAD_PHOTO_URL = `${API_URL}/file/upload/photo`
const PHOTO_URL = `${API_URL}/file/photo`
const ORG_URL = `${API_URL}/organisation`

const getContacts = (query: string): Promise<ContactsQueryResponse> => {
    return axios
        .get(`${GET_CONTACTS_URL}?${query}`)
        .then((d: AxiosResponse<ContactsQueryResponse>) => d.data)
}

const getContactById = (id: ID): Promise<Contact | undefined> => {
    return axios
        .get(`${CONTACT_URL}/${id}`)
        .then((response: AxiosResponse<Response<Contact>>) => response.data)
        .then((response: Response<Contact>) => response.data)
}

const uploadPhoto = (file: File): Promise<UploadedFile | undefined> => {
    let formData = new FormData()
    formData.append('photo', file)

    return axios
        .post(`${UPLOAD_PHOTO_URL}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then((response: AxiosResponse<UploadedFile>) => response.data)
}

const updatePhoto = (id: ID, uploadFileId: string): Promise<Contact | undefined> => {
    return axios
        .put(`${CONTACT_URL}/${id}/photo`, { uploadFileId: uploadFileId })
        .then((response: AxiosResponse<Response<Contact>>) => response.data)
        .then((response: Response<Contact>) => response.data)
}

const getPhotoLink = (filename: string): string => {
    return PHOTO_URL + '/' + filename
}

const createContact = (contact: Contact): Promise<Contact | undefined> => {
    return axios
        .post(CONTACT_URL, contact)
        .then((response: AxiosResponse<Response<Contact>>) => response.data)
        .then((response: Response<Contact>) => response.data)
}

// The bulk endpoint upserts by email: a row whose address already belongs to a
// contact updates that contact, everything else is created. It reports how the
// rows split so the import modal can say what actually happened.
type ImportResult = {
    message: string
    created: number
    updated: number
    // New contacts that could not be created because the row had no first name
    skipped: number
    total: number
}

// Dry run: asks the server what each row would do, without writing anything.
// Row numbers are 1-based and line up with the order the rows were sent in.
type ImportPreviewRow = {
    row: number
    action: 'create' | 'update' | 'skip'
    // Why a row is skipped — currently only a new contact with no first name
    reason?: 'noFirstName'
    matchedEmail?: string
    matchedContact?: { id: string; name: string; organisation: string }
    // Set when an earlier row in the same file creates the contact this row updates
    duplicateOfRow?: number
    noEmail?: boolean
    // Stored field names this row would overwrite. Empty means the row matches
    // an existing contact but every value is already the same.
    changedFields?: string[]
}

type ImportPreview = {
    rows: ImportPreviewRow[]
    created: number
    updated: number
    // Subset of `updated` whose stored values already match the sheet
    unchanged: number
    skipped: number
    total: number
}

const previewImportContact = (contacts: Contact[]): Promise<ImportPreview> => {
    return axios
        .post(PREVIEW_IMPORT_URL, contacts)
        .then((response: AxiosResponse<ImportPreview>) => response.data)
}

const importContact = (contacts: Contact[]): Promise<ImportResult> => {
    return axios
        .post(IMPORT_CONTACT_URL, contacts)
        .then((response: AxiosResponse<ImportResult>) => response.data)
}

const updateContact = (contact: Contact): Promise<Contact | undefined> => {
    return axios.put(`${CONTACT_URL}/${contact.id}`, contact)
}

const reactivateContact = (contactId: ID): Promise<string> => {
    return axios
        .put(`${CONTACT_URL}/${contactId}/reactivate`)
        .then((data: AxiosResponse<string>) => data.data)
}

const deleteContact = (contactId: ID): Promise<void> => {
    return axios.delete(`${CONTACT_URL}/${contactId}`).then(() => {})
}

const deleteSelectedContacts = (contactIds: Array<ID>): Promise<void> => {
    const requests = contactIds.map((id) => axios.delete(`${CONTACT_URL}/${id}`))
    return axios.all(requests).then(() => {})
}

const getOrganisation = (): Promise<any> => {
    return axios
        .get(`${ORG_URL}?`)
        .then((d: AxiosResponse<Response<ORG[]>>) => d.data)
        .then((d: Response<ORG[]>) => d.data)
}

export {
    getContacts,
    reactivateContact,
    deleteContact,
    deleteSelectedContacts,
    getContactById,
    createContact,
    importContact,
    previewImportContact,
    updateContact,
    uploadPhoto,
    updatePhoto,
    getPhotoLink,
    getOrganisation,
}
export type { ImportResult, ImportPreview, ImportPreviewRow }
